import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomAlertStore } from "../Store/store";
import {
  Main,
  Container,
  Header,
  Form,
  InputGroup,
  ErrorMessage,
  Button,
  LoginLink,
  LoadingSpinner,
} from "./RegisterPage/RegisterPage.styles";
import { resetPassword } from "../api/userManagementAPI";

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(-2px);
  }

  &:before {
    content: "←";
    font-size: 16px;
  }
`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccessAlert, showErrorAlert } = useCustomAlertStore();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // URL에서 토큰 추출
  const token = searchParams.get("token");

  useEffect(() => {
    // 토큰이 없으면 로그인 페이지로 리다이렉트
    if (!token) {
      showErrorAlert("잘못된 접근", "유효하지 않은 접근입니다.");
      navigate("/login");
    }
  }, [token, navigate, showErrorAlert]);

  const validateForm = () => {
    const newErrors = {};

    // 비밀번호 검증
    if (!formData.password) {
      newErrors.password = "새 비밀번호를 입력해주세요.";
    } else if (formData.password.length < 8) {
      newErrors.password = "비밀번호는 8자 이상이어야 합니다.";
    }

    // 비밀번호 확인 검증
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호 확인을 입력해주세요.";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호가 일치하지 않습니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // 실시간 에러 제거
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await resetPassword(formData.password, token);

      setTimeout(() => {
        setIsLoading(false);
        showSuccessAlert(
          "비밀번호 변경 완료",
          "비밀번호가 성공적으로 변경되었습니다. 새 비밀번호로 로그인해주세요.",
        );
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("비밀번호 재설정 실패:", error);
      showErrorAlert(
        "비밀번호 변경 실패",
        "비밀번호 변경에 실패했습니다. 다시 시도해주세요.",
      );
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate("/login");
  };

  const isFormValid =
    formData.password &&
    formData.confirmPassword &&
    Object.keys(errors).length === 0;

  return (
    <Main>
      <BackButton onClick={handleBackClick}>뒤로가기</BackButton>

      <Container>
        <Header className="project-header">
          <h1>비밀번호 재설정</h1>
          <p>새로운 비밀번호를 입력해주세요.</p>
        </Header>

        <Form onSubmit={handleSubmit}>
          <InputGroup error={errors.password}>
            <label htmlFor="password">새 비밀번호 *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="8자 이상 입력해주세요"
              disabled={isLoading}
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
          </InputGroup>

          <InputGroup error={errors.confirmPassword}>
            <label htmlFor="confirmPassword">비밀번호 확인 *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="비밀번호를 다시 입력해주세요"
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword}</ErrorMessage>
            )}
          </InputGroup>

          <Button type="submit" disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner /> 변경중...
              </>
            ) : (
              "비밀번호 변경"
            )}
          </Button>
        </Form>

        <LoginLink>
          <Link to="/login">로그인 페이지로 돌아가기</Link>
        </LoginLink>
      </Container>
    </Main>
  );
}
